import axios from "axios";

// Use deployed API if available, fallback to localhost
const API_BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:5000").replace(/\/$/, "");

// Create an axios instance with timeout
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout
  validateStatus: function (status) {
    return status >= 200 && status < 500; // Don't reject on 4xx errors
  }
});

console.log("API initialized with baseURL:", API_BASE_URL);

// Helper function to normalize event data from API
const normalizeEvent = (event) => {
  return {
    ...event,
    id: event._id || event.id, // Ensure id is available for compatibility
    // Convert date strings to proper format if needed
    date: event.date ? event.date : new Date().toISOString().split("T")[0],
    // Ensure other required fields have defaults
    recurring: typeof event.recurring === "boolean" ? event.recurring : false,
    recurrenceType: event.recurrenceType || "weekly",
  };
};

// Add request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor for logging
api.interceptors.response.use(
  (response) => {
    console.log(`API Response: ${response.status} from ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error("API Response Error:", error.message);
    if (error.response) {
      console.error("Status:", error.response.status);
      console.error("Data:", error.response.data);
    }
    return Promise.reject(error);
  }
);

// Mock API using localStorage for testing
const STORAGE_KEY = "schedule";

// Helper function to get data from localStorage
const getStoredData = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved ? JSON.parse(saved) : [];
};

// Helper function to save data to localStorage
const saveData = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

// Fetch all events
export const fetchSchedule = async () => {
  try {
    console.log("📡 Attempting to fetch events from API at:", API_BASE_URL);
    const response = await api.get('/api/schedule');
    
    console.log("📨 API response received:", response.status);
    
    if (response && response.data && Array.isArray(response.data)) {
      console.log("✅ Successfully fetched events from API:", response.data.length, "events");
      
      // Sync API data to localStorage as backup
      saveData(response.data);
      console.log("💾 Synced API data to localStorage");
      
      return response.data;
    } else if (response && response.status === 200) {
      console.log("✅ API returned 200 OK");
      const data = Array.isArray(response.data) ? response.data : [];
      saveData(data);
      return data;
    } else {
      throw new Error(`Invalid response from API: status ${response.status}`);
    }
  } catch (error) {
    console.warn("⚠️ API fetch failed, falling back to localStorage:", error.message);
    
    if (error.code === 'ECONNREFUSED' || error.code === 'ECONNABORTED') {
      console.warn("❌ Backend server is not running on port 3001");
    } else if (error.message === 'Network Error' || error.message.includes('timeout')) {
      console.warn("⚠️ Network error or timeout");
    } else if (error.response) {
      console.warn("⚠️ API responded with status:", error.response.status);
    }
    
    console.log("📦 Using localStorage data as fallback");
    const localData = getStoredData();
    console.log("Retrieved", localData.length, "events from localStorage");
    return localData;
  }
};

// Create a new event
export const createEvent = async (eventData) => {
  const newEvent = {
    ...eventData,
    _id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
  
  // Try API first
  try {
    console.log("📡 Creating event via API:", {
      course: eventData.course,
      date: eventData.date,
      createdBy: eventData.createdByName
    });
    const response = await api.post('/api/schedule', eventData);
    
    console.log("📨 API response:", response.status);
    
    if (response && response.data) {
      console.log("✅ Event created on API with ID:", response.data._id || response.data.id);
      
      // Save to localStorage for backup
      const events = getStoredData();
      events.push(response.data);
      saveData(events);
      
      return response.data;
    }
  } catch (error) {
    console.error("❌ API create failed:", error.message);
  }
  
  // Fallback: save to localStorage only
  console.log("📦 Saving event to localStorage only");
  const events = getStoredData();
  events.push(newEvent);
  saveData(events);
  console.log("✅ Event saved to localStorage with ID:", newEvent._id);
  
  return newEvent;
};

// Delete an event
export const deleteEvent = async (eventId) => {
  // Always delete from localStorage first (optimistic update)
  const events = getStoredData();
  const originalLength = events.length;
  const updatedEvents = events.filter(
    (event) => event._id !== eventId && event.id !== eventId
  );
  
  if (updatedEvents.length === originalLength) {
    console.warn("Event not found in localStorage for deletion:", eventId);
    return { success: false, message: "Event not found" };
  }
  
  saveData(updatedEvents);
  console.log("✅ Event deleted from localStorage");
  
  // Try to sync with API in background
  try {
    console.log("Deleting event from API:", eventId);
    const response = await api.delete(`/api/schedule/${eventId}`);
    
    if (response) {
      console.log("✅ Delete synced to API");
      return response.data;
    }
  } catch (error) {
    console.warn("⚠️ API sync failed, but localStorage updated:", error.message);
    console.error("Full error:", error);
  }
  
  return { success: true, message: "Event deleted" };
};

// Update an existing event
export const updateEvent = async (eventId, eventData) => {
  // Try API first
  try {
    console.log("📡 Updating event in API:", eventId);
    const response = await api.put(`/api/schedule/${eventId}`, eventData);
    
    if (response && response.data) {
      console.log("✅ Event updated on API");
      
      // Update localStorage to match API
      const events = getStoredData();
      const eventIndex = events.findIndex(
        (event) => event._id === eventId || event.id === eventId
      );
      if (eventIndex !== -1) {
        events[eventIndex] = response.data;
        saveData(events);
      }
      
      return response.data;
    }
  } catch (error) {
    console.error("❌ API update failed:", error.message);
  }
  
  // Fallback: update localStorage only
  const events = getStoredData();
  const eventIndex = events.findIndex(
    (event) => event._id === eventId || event.id === eventId
  );
  
  if (eventIndex === -1) {
    console.error("Event not found:", eventId);
    throw new Error('Event not found');
  }
  
  const updatedEvent = {
    ...eventData,
    _id: events[eventIndex]._id,
    id: events[eventIndex].id,
    createdAt: events[eventIndex].createdAt,
    updatedAt: new Date().toISOString()
  };
  
  events[eventIndex] = updatedEvent;
  saveData(events);
  console.log("📦 Event updated in localStorage only with ID:", updatedEvent._id);
  
  return updatedEvent;
};

// Clear all events
export const clearAllEvents = async () => {
  try {
    // Try API first
    const response = await api.delete('/api/schedule');
    console.log("Successfully cleared all events via API");
    
    // Also clear localStorage for consistency
    localStorage.removeItem(STORAGE_KEY);
    
    return response.data;
  } catch (error) {
    console.error("API error, falling back to localStorage:", error.message);
    console.log("Clearing all events from localStorage as fallback");
    
    // Fall back to localStorage if API fails
    localStorage.removeItem(STORAGE_KEY);
    console.log("All events cleared from localStorage");
    return { success: true, message: "All events cleared from localStorage" };
  }
};

// Test function to verify API connection
export const testApiConnection = async () => {
  try {
    console.log("Testing API connection to:", API_BASE_URL);
    
    // Try axios first
    const response = await api.get("/api/schedule/test");
    
    console.log("API test response:", response.status, response.data);
    
    if (response && response.data && response.status === 200) {
      console.log("✅ API connection successful via axios");
      return { connected: true, ...response.data };
    }
    
    // Fallback: try fetch API directly
    console.log("Axios check inconclusive, trying fetch...");
    const fetchResponse = await fetch(`${API_BASE_URL}/test`);
    const fetchData = await fetchResponse.json();
    
    if (fetchResponse.ok) {
      console.log("✅ API connection successful via fetch");
      return { connected: true, ...fetchData };
    }
    
    return { connected: false, message: 'Invalid response from both methods' };
  } catch (error) {
    console.warn("API connection test failed:", error.message);
    
    // Last ditch attempt: try fetch without error throwing
    try {
      console.log("Trying direct fetch as final attempt...");
      const finalResponse = await fetch(`${API_BASE_URL}/test`);
      if (finalResponse.ok) {
        const finalData = await finalResponse.json();
        console.log("✅ API connection successful on final attempt");
        return { connected: true, ...finalData };
      }
    } catch (fetchError) {
      console.error("Final fetch attempt failed:", fetchError.message);
    }
    
    console.error("All connection tests failed:", error);
    return { connected: false, message: error.message };
  }
};

// Check if API is available and return status
export const getApiStatus = async () => {
  console.log("🔍 Checking API status...");
  try {
    const result = await testApiConnection();
    console.log("Test connection result:", result);
    
    if (result.connected || result.message === 'Backend API is running!' || result.message?.includes('running')) {
      console.log("✅ API is ONLINE");
      return { 
        status: 'online', 
        message: '🟢 API connected - changes will be saved to server and localStorage',
        apiConnected: true 
      };
    } else {
      console.log("⚠️ API is OFFLINE");
      return { 
        status: 'offline', 
        message: '🟡 API offline - changes will be saved to localStorage only',
        apiConnected: false 
      };
    }
  } catch (error) {
    console.error("❌ API status check error:", error);
    return { 
      status: 'error', 
      message: '🔴 API unavailable - using localStorage for data storage',
      apiConnected: false 
    };
  }
};

// Search events based on query
export const searchEvents = async (query) => {
  try {
    // For a real API, you would use fetch here
    // const response = await api.get(`/events/search?q=${encodeURIComponent(query)}`);
    // return response.data;

    // For now, use localStorage and filter events locally
    const events = getStoredData();
    const searchTerm = query.toLowerCase();

    // Filter events based on various properties
    const filteredEvents = events.filter((event) => {
      // Search in title
      if (event.title && event.title.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in description
      if (
        event.description &&
        event.description.toLowerCase().includes(searchTerm)
      ) {
        return true;
      }

      // Search in location
      if (event.location && event.location.toLowerCase().includes(searchTerm)) {
        return true;
      }

      // Search in date
      if (event.date && event.date.includes(searchTerm)) {
        return true;
      }

      // Search in time
      if (event.time && event.time.toLowerCase().includes(searchTerm)) {
        return true;
      }

      return false;
    });

    return filteredEvents.map(normalizeEvent);
  } catch (error) {
    console.error("Error searching events:", error);
    throw error;
  }
};
